import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axiosInstance from "../axiosinstance";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import API_URL from "../Config";

const EditFlanges = ({
  isSidebarExpanded,
  flangeIdTickets,
  onClose,
  onAfterSavePopup,
}) => {
  const { flange_id } = useParams();
  const navigate = useNavigate();
  const [timezones, setTimezones] = useState([]);
  const [timezoneLoading, setTimezoneLoading] = useState(false);
  const [timezoneError, setTimezoneError] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState("");
  const [snackbarMessage, setSnackbarMessage] = useState("");
  const [severity, setSeverity] = useState("success");
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [editProcessId, setEditProcessId] = useState(null);
  const [fieldTypes, setFieldTypes] = useState({});

  const [formData, setFormData] = useState({
    equipment_name: "",
    equipment_number: "",
    stud_nut_moc: "",
    flange_size: "",
    bolt_diameter: "",
    required_torque: "",
    method_of_tightening: "",
    rating: "",
    flange_type: "",
    flange_size_unit: "",
    gasket_moc: "",
    stud_size: "",
    stud_size_unit: "",
    stud_length: "",
    stud_length_unit: "",
    spanner_size: "",
    spanner_size_unit: "",
    number_of_studs: "",
    lubricant_used: "",
  });

  // ---- Type + value helpers (no hard-coding) ----  ⭐ ADDED
  const isBlank = (v) =>
    v === undefined || v === null || String(v).trim() === "";

  // Convert backend value → controlled input string
  const toInputValue = (v) => (v === null || v === undefined ? "" : String(v));

  // Map backend object to your form state (strings for inputs)
  const mapBackendToForm = (obj) => {
    const out = {};
    Object.keys(obj || {}).forEach((k) => {
      if (k.startsWith("_")) return; // skip meta keys if any
      out[k] = toInputValue(obj[k]);
    });
    return out;
  };

  // Infer field types from a single backend record
  // number -> int/float; boolean -> boolean; string -> string; null/undefined => "unknown"
  const inferFieldTypesFromRecord = (rec = {}) => {
    const types = {};
    for (const [k, v] of Object.entries(rec)) {
      if (k.startsWith("_")) continue;
      if (v === null || v === undefined) {
        types[k] = "unknown";
      } else if (typeof v === "number") {
        types[k] = Number.isInteger(v) ? "int" : "float";
      } else if (typeof v === "boolean") {
        types[k] = "boolean";
      } else {
        types[k] = "string";
      }
    }
    return types;
  };

  // Optional smart detection for unknowns  ⭐ ADDED
// Detects numeric strings incl. decimals and scientific notation
const looksNumeric = (s) => /^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(s.trim());

const toTypedValue = (name, value, fieldTypes) => {
  if (isBlank(value)) return null;

  const t = fieldTypes?.[name] || "unknown";
  const s = String(value).trim();

  // Booleans (explicit or inferred)
  if (t === "boolean" || /^(true|false)$/i.test(s)) {
    return /^true$/i.test(s);
  }

  // Numbers: preserve decimals if the user typed them
  if (t === "int" || t === "float" || looksNumeric(s)) {
    const n = Number(s);
    if (!Number.isFinite(n)) return null;

    // If it was inferred as int AND the input is an integer, keep it integer.
    // Otherwise (user typed decimals), pass the float through.
    return t === "int" && Number.isInteger(n) ? n : n;
  }

  // Fallback: keep as string
  return value;
};


  const buildTypedPayload = (form, fieldTypes) =>
    Object.fromEntries(
      Object.entries(form).map(([k, v]) => [k, toTypedValue(k, v, fieldTypes)])
    );

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  // Fetch data on mount
  useEffect(() => {
    const idToFetch = flange_id || flangeIdTickets; // Use flange_id first, fallback to flangeIdTickets
    if (!idToFetch) return;

    const fetchFlange = async () => {
      try {
        const res = await axiosInstance.get(`/api/get_flange/${idToFetch}`);
        console.log("Fetched flange:", res.data);
        const server = res.data || {};
        setFieldTypes(inferFieldTypesFromRecord(server));
        setFormData((prev) => ({ ...prev, ...mapBackendToForm(server) }));
        setLoading(false);
      } catch (err) {
        console.error("Error fetching flange:", err);
        setError("Failed to load flange data.");
        setLoading(false);
      }
    };

    fetchFlange();
  }, [flange_id, flangeIdTickets]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  const fetchTimezones = async () => {
    setTimezoneLoading(true);
    setTimezoneError("");

    try {
      const response = await axiosInstance.get(`${API_URL}/api/all_timezones`);

      if (response.status === 200 && response.data) {
        setTimezones(response.data.timezones || []);
      } else {
        setTimezoneError(response.data.message || "Failed to load timezones.");
      }
    } catch (error) {
      console.error("Error fetching timezones:", error);
      if (error.response) {
        setTimezoneError(
          error.response.data.message || "Server error occurred."
        );
      } else if (error.request) {
        setTimezoneError("No response from server.");
      } else {
        setTimezoneError("Unexpected error occurred.");
      }
    } finally {
      setTimezoneLoading(false);
    }
  };

  useEffect(() => {
    fetchTimezones();
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbarOpen(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const idToUpdate = flange_id || flangeIdTickets;

    try {
      const payload = buildTypedPayload(formData, fieldTypes);
      await axiosInstance.put(`${API_URL}/api/edit_flange/${idToUpdate}`, payload);

      console.log("Process updated");
      setSeverity("success");
      setSnackbarMessage("Flange Updated Successfully");
      setSnackbarOpen(true);

      if (flange_id) {
        // ✅ Case 1: Edit via page → Navigate
        navigate("/flanges");
      } else if (flangeIdTickets) {
        // ✅ Case 2: Edit via Ticket → Show another popup
        if (onAfterSavePopup) {
          onAfterSavePopup(); // Trigger parent to open next popup
        }
        onClose(); // Close current popup
      }
    } catch (err) {
      console.error("Error updating flange:", err);
      setSeverity("error");
      setSnackbarMessage("Update failed");
      setSnackbarOpen(true);
    }
  };

  const handleCancel = () => {
    if (flange_id) {
      // If opened via flange_id, navigate to flanges page
      navigate("/flanges");
    } else {
      // If opened via ticket, just close the popup
      onClose(); // Assuming you have onClose prop to close popup
    }
  };

  if (loading) return <p>Loading flange data...</p>;
  if (error) return <p style={{ color: "red" }}>{error}</p>;

  return (
    <div
      className={`table-container ${
        isSidebarExpanded ? "expanded" : "collapsed"
      }`}

      style={{padding:"unset !important",minHeight:"unset !important", borderRadius:"0px"}}
    >
      <div className="table-container-content">
        <div className=" ">
          <div className=" form-body p-4">
            <h4 className="mb-1 fw-bold">Flange Information</h4>
            <p className="text-muted mb-2">Edit the flange details below</p>

            <form onSubmit={handleSubmit}>
              <div className="row">
                {/* Equipment Name */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="equipment_name"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Equipment Name
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="equipment_name"
                    name="equipment_name"
                    value={formData.equipment_name || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* Equipment Number */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="equipment_number"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Equipment Number
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="equipment_number"
                    name="equipment_number"
                    value={formData.equipment_number || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* Flange Size */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="flange_size"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Flange Size
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="flange_size"
                    name="flange_size"
                    value={formData.flange_size || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* Required Torque */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="required_torque"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Required Torque
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="required_torque"
                    name="required_torque"
                    value={formData.required_torque || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* Stud Nut MOC */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="stud_nut_moc"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Stud Nut MOC
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="stud_nut_moc"
                    name="stud_nut_moc"
                    value={formData.stud_nut_moc || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* Bolt Diameter */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="bolt_diameter"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Bolt Diameter
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="bolt_diameter"
                    name="bolt_diameter"
                    value={formData.bolt_diameter || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* Method of Tightening */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="method_of_tightening"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Method of Tightening
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="method_of_tightening"
                    name="method_of_tightening"
                    value={formData.method_of_tightening || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* Rating */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="rating"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Rating
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="rating"
                    name="rating"
                    value={formData.rating || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* flange_type */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="rating"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Flange Type
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="flange_type"
                    name="flange_type"
                    value={formData.flange_type || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* flange_size_unit */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="rating"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Flange Size Unit
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="flange_size_unit"
                    name="flange_size_unit"
                    value={formData.flange_size_unit || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* gasket_moc */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="rating"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Gasket MOC
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="gasket_moc"
                    name="gasket_moc"
                    value={formData.gasket_moc || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* stud_size */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="rating"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Stud Size
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="stud_size"
                    name="stud_size"
                    value={formData.stud_size || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* stud_size_unit */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="rating"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Stud Size Unit
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="stud_size_unit"
                    name="stud_size_unit"
                    value={formData.stud_size_unit || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* stud_length */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="rating"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Stud Length
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="stud_length"
                    name="stud_length"
                    value={formData.stud_length || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* stud_length_unit */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="rating"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Stud Length Unit
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="stud_length_unit"
                    name="stud_length_unit"
                    value={formData.stud_length_unit || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* spanner_size */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="rating"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Spanner Size
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="spanner_size"
                    name="spanner_size"
                    value={formData.spanner_size || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* spanner_size_unit */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="rating"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Spanner Size Unit
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="spanner_size_unit"
                    name="spanner_size_unit"
                    value={formData.spanner_size_unit || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* number_of_studs */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="rating"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Number of Studs
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="number_of_studs"
                    name="number_of_studs"
                    value={formData.number_of_studs || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* lubricant_used */}
                <div className="col-md-6 mb-3">
                  <label
                    htmlFor="rating"
                    className="form-label text-capitalize fw-semibold"
                  >
                    Lubricant Used
                  </label>
                  <input
                    type="text"
                    className="form-control"
                    id="lubricant_used"
                    name="lubricant_used"
                    value={formData.lubricant_used || ""}
                    onChange={handleChange}
                  />
                </div>

                {/* Tag */}
                {/* <div className="col-md-6 mb-3">
      <label htmlFor="tag" className="form-label text-capitalize fw-semibold">
        Tag
      </label>
      <input
        type="text"
        className="form-control"
        id="tag"
        name="tag"
        value={formData.tag || ""}
        onChange={handleChange}
      />
    </div> */}

                {/* Unit Manager */}
                {/* <div className="col-md-6 mb-3">
      <label htmlFor="unit_manager" className="form-label text-capitalize fw-semibold">
        Unit Manager
      </label>
      <input
        type="text"
        className="form-control"
        id="unit_manager"
        name="unit_manager"
        value={formData.unit_manager || ""}
        onChange={handleChange}
      />
    </div> */}

                {/* Timezone Selector */}
                <div className="col-md-6 mb-3">
                  {/* <label htmlFor="timezone" className="form-label fw-semibold">
    Timezone
  </label>
  <select
    id="timezone"
    name="timezone"
    className="form-select"
    value={formData.timezone || ""}
    onChange={handleChange}
    disabled={timezoneLoading}
    required
  >
    <option value="" disabled>
      -- Select Timezone --
    </option>

    {timezones.length > 0 ? (
      timezones.map((tz) => (
        <option key={tz} value={tz}>
          {tz}
        </option>
      ))
    ) : (
      !timezoneLoading && <option disabled>No timezones available</option>
    )}
  </select>

  {timezoneError && (
    <div className="form-text text-danger mt-1">{timezoneError}</div>
  )} */}
                </div>
              </div>

              {/* Buttons */}
              <div className="d-flex justify-content-end mt-4 gap-2">
                <button
                  type="button"
                  className="btn btn-light border"
                  onClick={handleCancel}
                >
                  Cancel
                </button>
                <button type="submit" className="btn btn-primary px-3">
                  Save
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        style={{ marginTop: "35px", marginLeft: "20px" }}
      >
        <MuiAlert onClose={handleSnackbarClose} severity={severity}>
          {snackbarMessage}
        </MuiAlert>
      </Snackbar>
    </div>
  );
};

export default EditFlanges;
